/**
 * Opaque reference codes for clickout failures.
 *
 * When a "Place Bet" clickout cannot proceed we must not surface the internal
 * cause to the punter — e.g. "no OC_API_KEY configured for repub WS" leaks
 * config detail and is meaningless to a user. Instead the error modal shows a
 * short, stable reference code (e.g. `BSX-1A2B`). The real cause lives here so a
 * support engineer can decode a reported code via {@link CLICKOUT_ERROR_LOOKUP};
 * the full detail is also written to the console for log correlation.
 *
 * Add a new failure mode by adding a key to {@link CLICKOUT_ERROR_REASONS}; its
 * code is derived automatically and the lookup stays in sync.
 */

/** Internal failure reasons, keyed by a stable identifier. The value is the human-readable detail (devs/support only — never shown to users). */
export const CLICKOUT_ERROR_REASONS = {
  MISSING_API_KEY: 'No OC_API_KEY configured for this repub — clickout aborted.',
} as const;

export type ClickoutErrorReason = keyof typeof CLICKOUT_ERROR_REASONS;

/**
 * djb2 → 4 uppercase hex chars. Deterministic, so a given reason always maps to
 * the same code and {@link CLICKOUT_ERROR_LOOKUP} stays in sync without any
 * hand-maintained constants. Keyed on the reason identifier (not its message),
 * so reworking the wording never changes a published code.
 */
const hashReason = (input: string): string => {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash + input.charCodeAt(i)) >>> 0;
  }
  return hash.toString(16).toUpperCase().padStart(8, '0').slice(0, 4);
};

/** The user-facing reference code for a failure reason, e.g. `BSX-1A2B`. */
export const getClickoutErrorCode = (reason: ClickoutErrorReason): string => `BSX-${hashReason(reason)}`;

/** code → { reason, detail }, for decoding a user-reported reference code back to its internal cause. */
export const CLICKOUT_ERROR_LOOKUP: Record<string, { detail: string; reason: ClickoutErrorReason }> =
  Object.fromEntries(
    (Object.keys(CLICKOUT_ERROR_REASONS) as ClickoutErrorReason[]).map(reason => [
      getClickoutErrorCode(reason),
      { detail: CLICKOUT_ERROR_REASONS[reason], reason },
    ]),
  );
